import * as fabric from 'fabric';
import { v4 as uuid } from 'uuid';
import { EventEmitter } from 'libs/utils/event-emitter';

import { BadgeTextbox } from './textbox';
import { BadgeQrCode } from './qr-code';
import { BadgeImage } from './image';
import { EditorState, sanitizeTextboxProps, TEXTBOX_PROPS, sanitizeQrCodeProps, QR_CODE_PROPS, sanitizeImageProps, IMAGE_PROPS } from './state';
import { DefaultQrCode, DefaultTextbox, DefaultImage } from 'shared/libs/providers/badge-editor/enum';

const MAX_ZOOM = 20;
const MIN_ZOOM = 0.01;

export class BadgeEditorController extends EventEmitter {
    /**
     * Create an editor instance
     *
     * @param {HTMLCanvasElement} canvas The canvas to render to
     * @param {HTMLDivElement} wrapper Wrapper of the canvas
     * @param {Record<string, unknown>} state
     * @param {Map<string, Image> | undefined} imageCache
     */
    constructor(canvas, wrapper, state, imageCache) {
        super();

        /** @type {Map<string,Image>} key is asset _id */
        this.imageCache = imageCache ?? new Map();

        /** @type  {light|dark} */
        this.theme = window.$cookies.get('theme') || 'light';

        /** @property @private {fabric.Canvas} canvas */
        this.canvas = new fabric.Canvas(canvas);

        this.state = new EditorState(state);

        /**
         * Handles theme changes
         *
         * @type {(CustomEvent<{theme: 'light'|'dark'}) => void>} event
         */
        this.themeChangeHandler = ({ detail: { theme } }) => {
            this.theme = theme;
            this.setupBackgroundColor();
        };

        this.setupBackgroundColor();
        this.setupState();
        this.setupResizeObserver(wrapper);
        this.setupThemeObserver();
        this.setupPanning();
        this.setupZooming();
        this.setupObjectTools();
        this.setupSelection();

        /** @type {{ backdrop: fabric.FabricObject | null, background: fabric.FabricObject | null, [key: string]: fabric.FabricObject | undefined }} */
        this.objects = {
            backdrop: null,
            background: null
        };

        // Default tool
        this.setTool('panning');

        // This object is special and static, excluded from most object logic
        this.addObject('backdrop', new fabric.Rect({
            id: 'backdrop',
            fill: '#ffffff',
            top: 0,
            left: 0,
            width: 100,
            height: 100,
            hasBorders: false,
            hasControls: false,
            hoverCursor: 'normal',
            selectable: false,
            lockMovementY: true,
            lockMovementX: true
        }));

        this.objects.backdrop.on('mousedown', () => {
            this.emit('selection', 'background');
        });
    }

    /** @type {() => void} */
    setupBackgroundColor() {
        this.canvas.backgroundColor = getComputedStyle(document.documentElement)
            .getPropertyValue(this.theme === 'dark' ? '--base-100' : '--base-300')
            .trim();
        this.canvas.renderAll();
    }

    /** @type {(id: string, image: Image) => void} */
    cacheImage(id, image) {
        this.imageCache.set(id, image);
    }

    /**
     * Update current state, create a new entry in state history and emit new state update
     *
     * @param {(newState: any) => void} callback Function that is called with deep cloned state to be modified
     * @emits EditorState#state
     */
    updateState(callback) {
        this.state.update(callback);
        this.syncState();
    }

    /**
     * Replace current state (when changing page)
     * @param {Record<string,unknown>} value
     */
    replaceState(value) {
        this.state.replace(value);
        this.syncState();
    }

    /**
     * Update object by id and type
     *
     * @param {string} id
     * @param {'text'|'qrcode'|'image'} type
     * @param {Record<string,string|number>} data
     */
    updateObjectById(id, type, data) {
        const idExists = this.objects[id];
        if (!idExists) return;

        // reset scaling
        if (type === 'qrcode') {
            const { width, height } = this.getImageFromCache('qrcode');
            data = this.scaleProps(data, { width, height });
        }
        if (type === 'image') {
            const { width, height } = this.getImageFromCache(data.id);
            data = this.scaleProps(data, { width, height });
        }

        this.updateState(state => {
            const object = (state.blocks[type] || []).find(({ _id }) => _id === id);
            if (!object) return;
            for (const [key, value] of Object.entries(data)) {
                if (object[key] !== data[key]) {
                    object[key] = value;
                }
            }
        });
    }

    /**
     * Remove object by id and type
     *
     * @param {string} id
     * @param {'text'|'qrcode'|'image'} type
     */
    removeObjectById(id, type) {
        this.updateState(state => {
            state.blocks[type] = (state.blocks[type] || []).filter(({ _id }) => _id !== id);
        });
    }

    /** @param {string} backgroundId */
    updateBackgroundImage(backgroundId) {
        this.updateState(state => {
            state.blocks.background.id = backgroundId;
        });
    }

    /** @param {{ id: string, imageId: string }} */
    updateImage({ id, imageId }) {
        this.updateState(state => {
            state.blocks.image = state.blocks.image.map(image => {
                if (image._id === id) {
                    const { width, height } = this.getImageFromCache(imageId);
                    image.id = imageId;
                    image.width = width;
                    image.height = height;
                }
                return image;
            });
        });
    }

    /**
     * Triggers JSON -> Fabric data flow, syncs Fabric to the current state
     */
    syncState() {
        // We upsert all objects from our state into Fabric
        this.syncStateTextBlocks();
        this.syncStateQrCodeBlocks();
        this.syncStateBackground();
        this.syncStateImageBlocks();

        // We remove all Fabric objects that are not in state (with exception of backdrop and background)
        this.syncStateRemove();
        this.canvas.renderAll();
    }

    /**
     * Sync text block data with Fabric
     */
    syncStateTextBlocks() {
        for (const text of this.state.current.blocks.text ?? []) {
            const data = sanitizeTextboxProps(text);

            if (!this.objects[text._id]) {
                this.addTextbox(data.text, data);
            }

            const object = this.objects[text._id];

            object.set(data);
            object.setCoords();
        }
    }

    /**
     * Sync background data with Fabric
     */
    syncStateBackground() {
        const background = this.state.current.blocks.background;
        if (background?.id) {
            if (this.objects.background && this.objects.background.id !== background.id) {
                this.canvas.remove(this.objects.background);
                delete this.objects.background;
            }

            if (!this.objects.background) {
                const imgElement = this.imageCache.get(background.id);
                this.addBackground(imgElement, background.id);
            }
        } else if (this.objects.background) {
            this.canvas.remove(this.objects.background);
            delete this.objects.background;
        }
    }

    /**
     * Sync qrcode block data with Fabric
     */
    syncStateQrCodeBlocks() {
        for (const qrcode of this.state.current.blocks.qrcode ?? []) {
            const imgElement = this.getImageFromCache('qrcode');
            const dimension = imgElement.width;
            const data = this.scaleProps(sanitizeQrCodeProps(qrcode), { width: dimension, height: dimension });

            if (!this.objects[qrcode._id]) {
                this.addQrCode(imgElement, data);
            }

            const object = this.objects[qrcode._id];

            object.set(data);
            object.setCoords();
        }
    }

    /**
     * Sync image block data with Fabric
     */
    syncStateImageBlocks() {
        for (const image of this.state.current.blocks.image ?? []) {
            const imgElement = this.getImageFromCache(image.id);
            const { width, height } = imgElement;
            const data = this.scaleProps(sanitizeImageProps(image), { width, height });
            let object = this.objects[image._id];

            if (object) {
                if (image.id !== object.id) {
                    object.setElement(imgElement);
                    data.width = width;
                    data.height = height;
                    data.scaleX = 1;
                    data.scaleY = 1;
                }
            } else {
                this.addImage(imgElement, data);
                object = this.objects[image._id];
            }

            object.set(data);
            object.setCoords();
        }
    }

    /**
     * Sync all removed elements
     */
    syncStateRemove() {
        const ids = this.state.getIds();
        for (const id of Object.keys(this.objects)) {
            if (id === 'backdrop' || id === 'background') {
                continue;
            }

            if (!ids.includes(id)) {
                this.canvas.remove(this.objects[id]);
                delete this.objects[id];
            }
        }
    }

    getImageFromCache(id) {
        const imageElement = this.imageCache.get(id);
        if (!imageElement) {
            throw new Error('Image needs to be loaded and stored in cache.');
        }
        return imageElement;
    }

    /**
     * @param {import('./state').IMAGE_PROPS|import('./state').QR_CODE_PROPS} props
     * @param {{width: number, height: number}} dimensions
     */
    scaleProps(props, dimensions) {
        const { width, height } = dimensions;
        if (props.width !== width) {
            props.scaleX = props.width / width;
            props.width = width;
        }
        if (props.height !== height) {
            props.scaleY = props.height / height;
            props.height = height;
        }
        return props;
    }

    /**
     * Trigger undo and a state sync
     *
     * @emits EditorState#state
     */
    undo() {
        this.state.undo();
        this.syncState();
    }

    /**
     * Trigger redo and a state sync
     *
     * @emits EditorState#state
     */
    redo() {
        this.state.redo();
        this.syncState();
    }

    /**
     * Add an object to the editor
     *
     * @param {string | undefined} id
     * @param {fabric.FabricObject} object
     * @returns id
     */
    addObject(id, object) {
        if (id === undefined) {
            id = uuid();
        }

        this.objects[id] = object;
        this.canvas.add(object);

        return id;
    }

    addBackground(imgElement, id) {
        const image = new fabric.FabricImage(imgElement, { top: 0, left: 0, selectable: false });

        image.set({
            id,
            hasBorders: false,
            hasControls: false,
            lockMovementY: true,
            lockMovementX: true
        });

        image.on('mousedown', () => {
            this.emit('selection', 'background');
        });

        this.addObject('background', image);
        this.canvas.sendObjectToBack(image);
        this.canvas.sendObjectToBack(this.objects.backdrop);
    }

    addImage(imgElement, props) {
        const image = new BadgeImage(imgElement, props || {});

        image.setControlsVisibility({
            ml: false,
            mt: false,
            mr: false,
            mb: false,
            mtr: false,
        });

        return this.addObject(props._id, image);
    }

    /**
     * Add an image to the canvas with events and properties setup
     *
     * @param {HTMLImageElement} imgElement Loaded img element
     * @param {import('./state').ImageProps} props
     */
    addQrCode(imgElement, props) {
        const qrcode = new BadgeQrCode(imgElement, props || {});

        qrcode.setControlsVisibility({
            ml: false,
            mt: false,
            mr: false,
            mb: false,
            mtr: false,
        });
        return this.addObject(props._id, qrcode);
    }

    /**
     * Add a textbox to the canvas with events and properties setup
     *
     * @param {string} text Initial text of the textbox
     * @param {import('./state').TextboxProps} props
     */
    addTextbox(text, props) {
        const textbox = new BadgeTextbox(text, { moveCursor: 'grabbing', hoverCursor: 'pointer', ...props });

        // We remove corners because they are troublesome
        textbox.setControlsVisibility({
            bl: false,
            br: false,
            tl: false,
            tr: false,
            mtr: false,
        });

        // Adding additional properties for handling of text size during resizing
        textbox.set({
            id: props._id,

            // recalculates font size - preferably in the preview mode.
            useDynamicFontSize: props.useDynamicFontSize,

            minFontSize: props.minFontSize,
            fontSize: props.fontSize,
            maxFontSize: props.maxFontSize ?? props.fontSize,
        });

        // This is a hack so that the height corners (bottom middle and top middle) resize the box instead of scaling the text
        const handleScaling = (() => {
            let lastHeight;

            return () => {
                if (textbox.__corner && textbox.__corner !== 'mr' && textbox.__corner !== 'ml') {
                    lastHeight = textbox.height * textbox.scaleY;
                }

                textbox.set({
                    height: lastHeight || textbox.height,
                    scaleY: 1,
                });

                textbox.recalculateFontSize();
            };
        })();

        textbox.once('added', () => {
            // scales font after editor canvas loads saved textbox.
            setTimeout(() => textbox.recalculateFontSize(), 0);
        });

        textbox.on('scaling', handleScaling);
        textbox.on('modified', handleScaling);
        textbox.on('editing:entered', handleScaling);
        textbox.on('changed', () => textbox.recalculateFontSize());
        textbox.on('resizing', () => textbox.recalculateFontSize());

        return this.addObject(props._id, textbox);
    }

    /**
     * Set current tool
     *
     * @param {'panning'|'textbox'|'qrcode'|'image'|'background'} tool
     * @emits BadgeEditorController#currentTool
     */
    setTool(tool) {
        this.currentTool = tool;
        const cursorByTool = {
            textbox: 'text',
            qrcode: 'crosshair',
            panning: 'grab',
            image: 'crosshair',
        };
        const cursor = cursorByTool[tool];
        if (cursor) {
            this.canvas.discardActiveObject();
            this.canvas.set({
                selection: false
            });
            this.setCursor(cursor);
        } else {
            this.setCursor('default');
        }
        this.canvas.renderAll();
    }

    /**
     * Convenience function to set current cursor
     *
     * @param {string} cursor
     */
    setCursor(cursor) {
        this.canvas.defaultCursor = cursor;
        this.canvas.hoverCursor = cursor;
        this.canvas.moveCursor = cursor;
        this.canvas.freeDrawingCursor = cursor;
        this.canvas.notAllowedCursor = cursor;
    }

    /**
     * Set the zoom to a value between MAX_ZOOM and MIN_ZOOM. The zoom focus is the center of the canvas
     *
     * @param {number} zoom
     * @emits BadgeEditorController#zoom
     */
    setZoom(zoom) {
        zoom = Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM);

        this.canvas.zoomToPoint(new fabric.Point(this.canvas.width / 2, this.canvas.height / 2), zoom);
        this.emit('zoom', zoom);
    }

    /**
     * Sets up ResizeObserver to resize the canvas when its parent changes dimensions
     *
     * @param {HTMLDivElement} wrapper Element wrapping the canvas
     */
    setupResizeObserver(wrapper) {
        this.resizeObserver = new ResizeObserver(this.recalculateDimensions.bind(this));
        this.resizeObserver.observe(wrapper);
        this.recalculateDimensions();
    }

    setupThemeObserver() {
        window.addEventListener('theme-changed', this.themeChangeHandler);
    }

    /**
     * Sets up mouse wheel zoom interaction
     *
     * @emits BadgeEditorController#zoom
     */
    setupZooming() {
        this.canvas.on('mouse:wheel', (event) => {
            const delta = event.e.deltaY;
            let zoom = this.canvas.getZoom();
            zoom *= 0.999 ** delta;
            if (zoom > MAX_ZOOM) zoom = MAX_ZOOM;
            if (zoom < MIN_ZOOM) zoom = MIN_ZOOM;

            this.canvas.zoomToPoint(event.viewportPoint, zoom);
            this.canvas.setZoom(zoom);

            this.emit('zoom', zoom);

            event.e.preventDefault();
            event.e.stopPropagation();
        });
    }

    /**
     * Sets up mouse panning tool interaction
     */
    setupPanning() {
        this.canvas.on('mouse:down', (downEvent) => {
            const Point = fabric.Point;
            const target = downEvent.target;
            if (this.currentTool === 'panning' && [undefined, this.objects.backdrop, this.objects.background].includes(target)) {
                let lastPoint = new Point(downEvent.e.screenX, downEvent.e.screenY);
                this.setCursor('grabbing');

                const unsubMove = this.canvas.on('mouse:move', (moveEvent) => {
                    const newPoint = new Point(moveEvent.e.screenX, moveEvent.e.screenY);
                    this.canvas.relativePan(newPoint.subtract(lastPoint));
                    lastPoint = newPoint;
                });

                const unsubEnd = this.canvas.on('mouse:up', (upEvent) => {
                    const newPoint = new Point(upEvent.e.screenX, upEvent.e.screenY);
                    this.canvas.relativePan(newPoint.subtract(lastPoint));
                    unsubEnd();
                    unsubMove();

                    this.setCursor('grab');
                });
            }
        });
    }

    /**
     * Sets up mouse object tool interactions and default values for when those objects are created
     */
    setupObjectTools() {
        this.canvas.on('mouse:down', (event) => {
            const pointer = this.canvas.getScenePoint(event.e);
            const id = uuid();

            switch (this.currentTool) {
                case 'textbox': {
                    this.updateState(state => {
                        if (!Array.isArray(state.blocks.text)) {
                            state.blocks.text = [];
                        }

                        state.blocks.text.push({
                            ...DefaultTextbox,
                            _id: id,
                            left: pointer.x,
                            top: pointer.y,
                        });
                    });
                    this.setTool('panning');

                    const subject = this.objects[id];
                    this.canvas.setActiveObject(subject, event);
                    this.canvas.renderAll();
                    this.activate([subject]);
                    break;
                }
                case 'qrcode': {
                    this.updateState(state => {
                        if (!Array.isArray(state.blocks.qrcode)) {
                            state.blocks.qrcode = [];
                        }

                        state.blocks.qrcode.push({
                            ...DefaultQrCode,
                            _id: id,
                            isQrCode: true,
                            left: pointer.x,
                            top: pointer.y,
                        });
                    });
                    this.setTool('panning');

                    const subject = this.objects[id];
                    this.canvas.setActiveObject(subject, event);
                    this.canvas.renderAll();
                    this.activate([subject]);
                    break;
                }
                case 'image': {
                    this.updateState(state => {
                        if (!Array.isArray(state.blocks.image)) {
                            state.blocks.image = [];
                        }

                        state.blocks.image.push({
                            ...DefaultImage,
                            _id: id,
                            isImage: true,
                            left: pointer.x,
                            top: pointer.y,
                            id: 'placeholder',
                        });
                        this.setTool('panning');
                    });
                    const subject = this.objects[id];
                    this.canvas.setActiveObject(subject, event);
                    this.canvas.renderAll();
                    this.activate([subject]);
                    break;
                }
                default:
                    return;
            }
        });
    }

    /**
     * Activates selection of a given subject
     */
    activate(subject) {
        requestAnimationFrame(() => {
            this.emit('selection', subject);
        });
    }

    /**
     * Sets up Fabric -> JSON data flow
     */
    setupState() {
        this.canvas.on('object:modified', (event) => {
            const target = event.target;

            if (target instanceof BadgeTextbox) {
                const textbox = target.toObject(TEXTBOX_PROPS);

                this.state.update((state) => {
                    const block = state.blocks.text.find(({ _id }) => _id === textbox._id);

                    if (block) {
                        Object.assign(block, sanitizeTextboxProps(textbox));
                    }
                });
                return;
            }

            if (target instanceof BadgeQrCode) {
                const qrCode = target.toObject(QR_CODE_PROPS);

                this.state.update((state) => {
                    const block = state.blocks.qrcode.find(({ _id }) => _id === qrCode._id);

                    if (block) {
                        Object.assign(block, sanitizeQrCodeProps(qrCode));
                    }
                });
                return;
            }

            if (target instanceof BadgeImage) {
                const image = target.toObject(IMAGE_PROPS);

                this.state.update((state) => {
                    const block = state.blocks.image.find(({ _id }) => _id === image._id);

                    if (block) {
                        Object.assign(block, sanitizeImageProps(image));
                    }
                });
            }
        });
    }

    /**
     * Sets up selection interactions
     *
     * @emits BadgeEditorController#selection
     */
    setupSelection() {
        const handleSelection = (event) => {
            const selected = event.selected || [];
            const deselected = event.deselected || [];

            const emit = () => this.emit('selection', selected);
            emit();

            for (const object of selected) {
                object.on('modified', emit);
                object.on('moving', emit);
            }

            for (const object of deselected) {
                // Removal of placeholder images when deselected
                if (object.isImage && object.id === 'placeholder') {
                    this.canvas.remove(object);
                    this.removeObjectById(object._id, 'image');
                }
            }
        };

        this.canvas.on({
            'selection:created': handleSelection,
            'selection:updated': handleSelection,
            'selection:cleared': handleSelection,
        });
    }

    /**
     * Sets the dimensions of the backdrop
     *
     * @param {BackdropDetails} details
     */
    setBackdrop(width, height) {
        this.objects.backdrop.set({
            width: width,
            height: height
        });

        this.emit('backdrop', { width, height });

        this.objects.backdrop.setCoords();

        this.zoomToBackdrop();
    }

    /**
     * Returns the dimensions of the backdrop
     *
     * @returns {{width: number, height: number}}
     */
    getBackdrop() {
        return { width: this.objects.backdrop.width, height: this.objects.backdrop.height };
    }

    /**
     * Calculates static width and height that the canvas should have
     */
    recalculateDimensions() {
        const previousCenter = this.canvas.getCenterPoint();

        // We use CSS to fill the entire available space
        this.canvas.setDimensions({ width: '100%', height: '100%' });
        // Read what those numbers are
        const bbox = this.canvas.getElement().getBoundingClientRect();
        // And set them statically
        this.canvas.setDimensions({ width: bbox.width, height: bbox.height });

        const newCenter = this.canvas.getCenterPoint();

        // We make sure that our center point remains in the same place
        this.canvas.relativePan(newCenter.subtract(previousCenter));
    }

    /**
     * Pan and zoom to the backdrop so its visible in its entirety
     *
     * @emits BadgeEditorController#zoom
     */
    zoomToBackdrop() {
        // Always start from an untransformed canvas
        this.canvas.viewportTransform = [1, 0, 0, 1, 0, 0];

        const contentWidth = this.objects.backdrop.width + 60;
        const contentHeight = this.objects.backdrop.height + 60;

        const contentCenter = new fabric.Point(contentWidth / 2, contentHeight / 2);
        const canvasCenter = new fabric.Point(this.canvas.width / 2, this.canvas.height / 2);

        const difference = contentCenter.subtract(canvasCenter).subtract({ x: 30, y: 30 });
        this.canvas.absolutePan(difference);

        const zoom = Math.min(this.canvas.width / contentWidth, this.canvas.height / contentHeight);
        this.canvas.zoomToPoint(canvasCenter, zoom);

        this.emit('zoom', zoom);
        this.canvas.renderAll();
    }

    /**
     * Disposes the underlying Fabric object structure
     */
    dispose() {
        this.resizeObserver.disconnect();
        window.removeEventListener('theme-changed', this.themeChangeHandler);
    }
}
